<script setup>
import { computed, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, requiredIf } from '@vuelidate/validators';
import Input from 'next/input/Input.vue';

const { t } = useI18n();

const triggerTypes = [
  {
    key: 'custom_event_logged',
    label: t(
      'WGPT_CUSTOM_REPORTS.CREATE_ENRICHMENT_DIALOG.FORM.TYPE_OPTIONS.CUSTOM_EVENT_LOGGED'
    ),
    description: t(
      'WGPT_CUSTOM_REPORTS.CREATE_ENRICHMENT_DIALOG.FORM.TYPE_OPTIONS.CUSTOM_EVENT_LOGGED_DESC'
    ),
  },
  {
    key: 'event_enrichment_scheduled',
    label: t(
      'WGPT_CUSTOM_REPORTS.CREATE_ENRICHMENT_DIALOG.FORM.TYPE_OPTIONS.EVENT_ENRICHMENT_SCHEDULED'
    ),
    description: t(
      'WGPT_CUSTOM_REPORTS.CREATE_ENRICHMENT_DIALOG.FORM.TYPE_OPTIONS.EVENT_ENRICHMENT_SCHEDULED_DESC'
    ),
  },
];

const state = reactive({
  name: '',
  type: 'custom_event_logged',
  cron: '',
});

const rules = computed(() => ({
  name: { required },
  type: { required },
  cron: {
    required: requiredIf(() => state.type === 'event_enrichment_scheduled'),
  },
}));
const v$ = useVuelidate(rules, state);

const validate = async () => {
  await v$.value.$validate();
  return !v$.value.$invalid;
};

const nameError = computed(() =>
  v$.value.name.$error ? v$.value.name.$errors[0].$message : ''
);
const cronError = computed(() =>
  v$.value.cron.$error ? v$.value.cron.$errors[0].$message : ''
);

defineExpose({ state, validate });
</script>

<template>
  <div class="flex flex-col gap-4">
    <Input
      v-model="state.name"
      :label="t('WGPT_CUSTOM_REPORTS.CREATE_ENRICHMENT_DIALOG.FORM.NAME')"
      :placeholder="
        t('WGPT_CUSTOM_REPORTS.CREATE_ENRICHMENT_DIALOG.FORM.NAME_PLACEHOLDER')
      "
      :message="nameError"
      :message-type="nameError ? 'error' : 'info'"
      @blur="v$.name.$touch"
    />

    <div>
      <label class="text-sm font-medium text-n-slate-12 mb-1.5 block">
        {{ t('WGPT_CUSTOM_REPORTS.CREATE_ENRICHMENT_DIALOG.FORM.TYPE') }}
      </label>
      <div class="flex flex-col gap-2">
        <button
          v-for="option in triggerTypes"
          :key="option.key"
          type="button"
          class="flex flex-col text-left px-4 py-3 rounded-lg border transition-colors"
          :class="
            state.type === option.key
              ? 'border-n-brand bg-n-alpha-2 text-n-blue-11'
              : 'border-n-weak text-n-slate-11 hover:border-n-slate-7'
          "
          @click="state.type = option.key"
        >
          <span class="text-sm font-medium">{{ option.label }}</span>
          <span class="text-xs text-n-slate-10 mt-0.5">{{
            option.description
          }}</span>
        </button>
      </div>
    </div>

    <Input
      v-if="state.type === 'event_enrichment_scheduled'"
      v-model="state.cron"
      :label="t('WGPT_CUSTOM_REPORTS.CREATE_ENRICHMENT_DIALOG.FORM.CRON')"
      :placeholder="
        t('WGPT_CUSTOM_REPORTS.CREATE_ENRICHMENT_DIALOG.FORM.CRON_PLACEHOLDER')
      "
      :message="cronError"
      :message-type="cronError ? 'error' : 'info'"
      @blur="v$.cron.$touch"
    />
  </div>
</template>
